import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { SuperAdminGuard } from "@/components/shared/super-admin-guard";
import { useAppSettings, useUpdateAppSettings, useTestAccountsCount, useDeleteTestAccounts } from "@/lib/api/app-settings/hooks";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

function TestModeSettingsPage() {
  const { data: settings, isLoading, error } = useAppSettings();
  const { mutate: updateSettings, isPending } = useUpdateAppSettings();
  const { data: testAccountsData } = useTestAccountsCount();
  const { mutate: deleteAccounts, isPending: isDeleting } = useDeleteTestAccounts();

  const handleToggle = (enabled: boolean) => {
    updateSettings(
      { testModeEnabled: enabled },
      {
        onSuccess: () => {
          toast.success(
            enabled
              ? "테스트 모드가 활성화되었습니다."
              : "테스트 모드가 비활성화되었습니다."
          );
        },
        onError: () => {
          toast.error("설정 변경에 실패했습니다.");
        },
      }
    );
  };

  const handleDeleteTestAccounts = () => {
    deleteAccounts(undefined, {
      onSuccess: (data) => {
        toast.success(`${data.deletedCount}개의 테스트 계정이 삭제되었습니다.`);
      },
      onError: () => {
        toast.error("테스트 계정 삭제에 실패했습니다.");
      },
    });
  };

  const testAccountsCount = testAccountsData?.count ?? 0;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        로딩 중...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-destructive">
        설정을 불러오는데 실패했습니다.
      </div>
    );
  }

  return (
    <SuperAdminGuard>
      <PageContainer>
        <Header
          title="테스트 모드 설정"
          description="앱스토어 심사용 테스트 계정 기능을 관리합니다."
        />

        <Card>
          <CardHeader>
            <CardTitle>테스트 모드</CardTitle>
            <CardDescription>
              앱스토어 심사 시에만 활성화하세요. 활성화하면 다음 기능이 적용됩니다:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
              <li>테스트 전화번호(0100000xxxx)로 SMS 인증 우회 가능</li>
              <li>테스트 전화번호 중복 체크 제외</li>
              <li>테스트 계정(test01) 비밀번호 변경/탈퇴 차단</li>
              <li>테스트 전화번호 변경은 test01만 가능</li>
            </ul>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="test-mode-toggle" className="text-base">
                  테스트 모드 활성화
                </Label>
                <p className="text-sm text-muted-foreground">
                  {settings?.testModeEnabled
                    ? "현재 테스트 모드가 활성화되어 있습니다."
                    : "현재 테스트 모드가 비활성화되어 있습니다."}
                </p>
              </div>
              <Switch
                id="test-mode-toggle"
                checked={settings?.testModeEnabled ?? false}
                onCheckedChange={handleToggle}
                disabled={isPending}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>테스트 계정 관리</CardTitle>
            <CardDescription>
              테스트 전화번호(0100000xxxx)로 가입한 계정을 관리합니다.
              test01 계정은 삭제되지 않습니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base">테스트 계정 일괄 삭제</Label>
                <p className="text-sm text-muted-foreground">
                  현재 {testAccountsCount}개의 테스트 계정이 있습니다.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    disabled={isDeleting || testAccountsCount === 0}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    삭제
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>테스트 계정 삭제</AlertDialogTitle>
                    <AlertDialogDescription>
                      {testAccountsCount}개의 테스트 계정을 삭제하시겠습니까?
                      이 작업은 되돌릴 수 없습니다.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>취소</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteTestAccounts}>
                      삭제
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    </SuperAdminGuard>
  );
}

export const Route = createFileRoute("/_content/settings/test-mode")({
  component: TestModeSettingsPage,
});
